import { useEffect, useMemo } from "react";

import uniqBy from "lodash/uniqBy";

import { ConfigurableVariableOption } from "@regal-voice/ui";
import { useQueryContactAttributes } from "Pages/orchestration/Pages/journeys/JourneyEditPage/Components/JourneyBuilder/shared/hooks/useQueryContactAttributes";
import { toContactsFieldLookup } from "ServerData/EventSchema/Utils/eventSchemaUtils";

export type AdditionalContactOption = { label: string; value: string; dataType?: string };

/**
 * Fetches contact attributes and merges them with any additional (form-derived) options into
 * {@link ConfigurableVariableOption} instances ready for use in {@link ConfigurableVariablesPickerPlugin}.
 *
 * @param additionalContactOptions  Extra options derived from form state (e.g. "Define Contact Data").
 *                                  API-fetched options take priority over these when values collide.
 */
export function useContactVariableOptions(additionalContactOptions: AdditionalContactOption[] = []) {
    // @TODO replace with useContactAttributesFullListQuery, no need for the useEffect
    // @see src/ServerData/EventSchema/Hooks/useContactAttributesQuery/useContactAttributesQuery.ts
    const { loadContactAttributes, contactAttributes, loading } = useQueryContactAttributes({
        formatToObject: false,
    });

    useEffect(() => {
        loadContactAttributes();
    }, [loadContactAttributes]);

    const contactEventOptions = useMemo(() => toContactsFieldLookup(contactAttributes), [contactAttributes]);

    const options = useMemo<ConfigurableVariableOption[]>(() => {
        const fromApi = contactEventOptions.map(
            (option) => new ConfigurableVariableOption(option.label, option.value, option.dataType)
        );
        const apiValues = new Set(fromApi.map((o) => o.value));
        const fromCustom = additionalContactOptions
            .filter((c) => c.value && !apiValues.has(c.value))
            .map((c) => new ConfigurableVariableOption(c.label, c.value, c.dataType ?? "string"));
        return uniqBy([...fromApi, ...fromCustom], "value");
    }, [contactEventOptions, additionalContactOptions]);

    return { options, loading };
}
